import { useEffect } from 'react';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import { Anchor, FieldSet, Heading, Legend, Paragraph } from '@zillow/constellation-10';
import TermsCheckbox from './TermsCheckbox';
import {
  type GetTermSetWebApiResponseDto,
  IdentificationStatusType,
  TermType,
  type WebApiTerm,
} from '@zg-rentals/ts-id-verification-client';
import { GENERIC_ERROR_SUBSTEP } from '../../constants/steps';
import { useId, useIdVerificationCallbacks, useTermSet } from '../../contexts/IdVerificationContext';
import { LANDLORD_CANNOT_IDV_HELP_URL, RENTER_CANNOT_IDV_HELP_URL } from '../../constants/urls';
import { ExitReason, IdvStep, LogLevel, TermSet } from '../../../../models';
import IdvSpinner from '../../IdvSpinner';
import NoticeToUsers from './NoticeToUsers';
import { LANDLORD_CLICKSTREAM_EVENTS } from '../../clickstream/events';
import {
  IdVerificationWebApiHandler_getTermSet_queryOptions,
  useIdVerificationWebApiHandler_acceptTerms,
  useIdVerificationWebApiHandler_getTermSet,
  useIdVerificationWebApiHandler_isUserIdentified,
} from '@zg-rentals/ts-id-verification-client-react-query';
import { getZgAuthZoneFromTermSet } from '../../utils/zgAuthZoneUtils';
import { termSetToTermSetId } from '../../utils/termSetUtils';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useMount } from 'ahooks';

const TAC_OVERVIEW =
  'Before starting your ID verification, you’ll need to review and agree to our third party partners’ ' +
  'requirements for viewing personal information. This provides security for both you and your applicants.';

export function allTermsAccepted(terms: Array<WebApiTerm> | undefined) {
  if (!terms?.length) {
    return null;
  }
  return terms.every((term) => term.isAccepted && term.isCurrent);
}

export function getTerms(getTermSetData: GetTermSetWebApiResponseDto | undefined) {
  return getTermSetData?.terms ?? [];
}

interface TermStepProps {
  id: string;
  label: string;
}

export default function TermsStep({ id, label }: TermStepProps) {
  const queryClient = useQueryClient();
  const { onRequestStep } = useWizard();
  const termSet = useTermSet();
  const requestingClient = useId();
  const termSetId = termSetToTermSetId(termSet);
  const getTermSetKey = IdVerificationWebApiHandler_getTermSet_queryOptions({ termSetId }).queryKey;
  const { trackEvent, onExit, log } = useIdVerificationCallbacks();
  const {
    isLoading: getTermSetIsLoading,
    data: getTermSetData,
    isError: isGetTermSetError,
    error: getTermSetError,
  } = useIdVerificationWebApiHandler_getTermSet(
    { termSetId },
    {
      staleTime: 0,
    },
  );
  const terms = getTerms(getTermSetData);

  useMount(() => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.displayTerms);
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('idv_terms_and_conditions'));
  });

  useEffect(() => {
    if (isGetTermSetError) {
      log(LogLevel.Error, {
        message: 'Error retrieving term set',
        error: getTermSetError,
      });
      onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified for staleness
  }, [isGetTermSetError, onRequestStep]);

  const { isLoading: isIdentifiedLoading, data: isUserIdentifiedResponse } =
    useIdVerificationWebApiHandler_isUserIdentified({
      requestingClient,
      zgAuthZone: getZgAuthZoneFromTermSet(termSet),
    });

  const identificationStatusType = isUserIdentifiedResponse?.identificationStatusType;

  const {
    mutateAsync: markAcceptedTerms,
    isPending,
    isError,
  } = useIdVerificationWebApiHandler_acceptTerms({
    onMutate: async (body) => {
      const prevState = queryClient.getQueryData(getTermSetKey);
      queryClient.setQueryData(getTermSetKey, (previous: unknown) => {
        const prev = previous as GetTermSetWebApiResponseDto;
        const newData: GetTermSetWebApiResponseDto = {
          terms: prev.terms.map((termData) => {
            const matchingTerm = body?.postData.termsToAccept.find((term) => {
              return term.termType === termData.id;
            });

            if (matchingTerm) {
              return {
                ...termData,
                acceptedVersion: matchingTerm.version,
                isAccepted: matchingTerm.version === -1 ? false : true,
                isCurrent: matchingTerm.version === -1 ? false : true,
              };
            }

            return termData;
          }),
        };
        return newData;
      });
      return prevState;
    },
    onSettled() {
      queryClient.invalidateQueries({ queryKey: getTermSetKey });
    },
  });

  const writtenConsentTerms = terms.filter((term) => {
    return (
      term.id === TermType.EXPERIAN_CIC_WRITTEN_CONSENT_RENTER || term.id === TermType.EXPERIAN_WRITTEN_CONSENT_LANDLORD
    );
  });
  const otherTerms = terms.filter((term) => {
    return (
      term.id !== TermType.EXPERIAN_CIC_WRITTEN_CONSENT_RENTER && term.id !== TermType.EXPERIAN_WRITTEN_CONSENT_LANDLORD
    );
  });
  return (
    <WizardStep
      id={id}
      label={label}
      meta={{
        isNextDisabled: !allTermsAccepted(terms) || isPending || isError,
      }}
      onRequestNext={async (abortController: AbortController) => {
        abortController.abort();
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.completedAgreeingToAll);
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNext('idv_terms_and_conditions'));
        if (identificationStatusType === IdentificationStatusType.CURRENTLY_IDENTIFIED) {
          await onExit({ reason: ExitReason.CurrentlyIdentifiedAcceptedNewTerms, success: true });
        } else {
          onRequestStep(IdvStep.PersonalInfoStep);
        }
      }}
      render={() => {
        if (getTermSetIsLoading || isIdentifiedLoading) {
          return <IdvSpinner />;
        }
        return (
          <>
            <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
              Agree to terms and conditions
            </Heading>
            <Paragraph css={{ marginBottom: 'default' }}>
              {TAC_OVERVIEW}{' '}
              <Anchor
                href={termSet === TermSet.LandlordScreening ? LANDLORD_CANNOT_IDV_HELP_URL : RENTER_CANNOT_IDV_HELP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Read more about identity verification requirements
              </Anchor>
            </Paragraph>
            <Paragraph css={{ marginBottom: 'default' }}>
              Review the terms and policies carefully before agreeing to each statement.
            </Paragraph>
            <Paragraph css={{ marginBottom: 'default' }}>
              Zillow Group will not store or use any of the entered information.
            </Paragraph>
            <FieldSet required legend={<Legend>To continue, accept the following terms and policies:</Legend>}>
              <TermsCheckbox terms={writtenConsentTerms} markAcceptedTerms={markAcceptedTerms} />
              <TermsCheckbox terms={otherTerms} markAcceptedTerms={markAcceptedTerms} />
            </FieldSet>
            <NoticeToUsers />
          </>
        );
      }}
    />
  );
}
